import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@reflex/ui/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent } from '@reflex/ui/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@reflex/ui/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@reflex/ui/components/ui/dropdown-menu';
import { CodeBlock } from '@/components/content/CodeBlock';
import {
  Trash2,
  Pencil,
  Copy,
  MoreVertical,
  AlertTriangle,
  FileCode,
  Terminal,
  FolderTree,
  Layers,
  Play,
  Plus,
  RefreshCw,
} from 'lucide-react';
import { cn } from '@reflex/ui/lib/utils';
import { Spinner } from '@reflex/ui/components/shared/Spinner';
import { PanelHeader } from '@reflex/ui/components/layout/PanelHeader';
import { PanelTitle, PageDescription, SectionLabel } from '@reflex/ui/components/layout/Typography';
import { EmptyState } from '@reflex/ui/components/shared/EmptyState';
import { InfoPanel } from '@/components/shared/InfoPanel';
import { CollapsibleSection } from '@/components/shared/CollapsibleSection';
import {
  useDeleteBlueprint,
  useRebuildBlueprint,
  useBlueprintLogs,
  useBlueprints,
} from '@/hooks/blueprints/useBlueprints';
import { useBaseImage, useRebuildBaseImage } from '@/hooks/blueprints/useBaseImage';
import { useOrgRoutes } from '@/lib/useOrgRoutes';
import { displayBlueprintName, PUBLIC_BASE_BLUEPRINT_NAME } from '@reflex/shared';
import type { Blueprint } from '@/lib/types';
import { BLUEPRINT_LOAD_ERROR_TITLE, BLUEPRINT_LOAD_ERROR_DESCRIPTION } from '@/lib/blueprints';
import { BlueprintStatusBadge } from './blueprint-detail/BlueprintStatusBadge';
import { ArchBadge } from '@reflex/ui/components/shared/ArchBadge';
import { BlueprintDetailsSection } from './blueprint-detail/BlueprintDetailsSection';
import { CommandList } from './blueprint-detail/CommandList';
import { BuildLogsSection } from './blueprint-detail/BuildLogsSection';
import { BaseBlueprintLayersSection } from './blueprint-detail/BaseBlueprintLayersSection';

/**
 * Name of the host-managed base blueprint. When the selected blueprint
 * carries this name the detail panel switches into a layered view
 * (core / plugin / org overrides) and hides Edit + Delete since the
 * blueprint is owned by the bootstrap service.
 */
const BASE_BLUEPRINT_NAME = 'base';

interface BlueprintDetailPanelProps {
  blueprint: Blueprint;
  onDeleted: () => void;
  onSelectBlueprint?: (blueprint: Blueprint) => void;
}

export function BlueprintDetailPanel({
  blueprint,
  onDeleted,
  onSelectBlueprint,
}: BlueprintDetailPanelProps) {
  const navigate = useNavigate();
  const { data: blueprints = [] } = useBlueprints();
  const deleteBlueprint = useDeleteBlueprint();
  const rebuildBlueprint = useRebuildBlueprint();
  const rebuildBase = useRebuildBaseImage();
  // `currentStatus` tracks the newest derived-base build row, so it flips
  // to queued/provisioning/building while a force rebuild runs in the
  // background — `useBaseImage` polls every 5s during that window, so the
  // rebuild button re-enables itself when the build completes.
  const { data: baseImage } = useBaseImage();
  const baseBuildInFlight =
    baseImage?.currentStatus === 'queued' ||
    baseImage?.currentStatus === 'provisioning' ||
    baseImage?.currentStatus === 'building';
  const routes = useOrgRoutes();
  const isFinalized = blueprint.status === 'build_complete';
  const showLogs = blueprint.status === 'building' || blueprint.status === 'failed' || isFinalized;
  const isBaseBlueprint =
    blueprint.name === BASE_BLUEPRINT_NAME || blueprint.name === PUBLIC_BASE_BLUEPRINT_NAME;
  // A rebuild creates a NEW build row, so the selected base row stays
  // `build_complete` ("Ready") while the fresh build runs. Drive the
  // badge from the lineage's newest build status so it tracks the
  // in-flight rebuild alongside the disabled refresh action.
  const badgeStatus =
    isBaseBlueprint && baseImage?.currentStatus ? baseImage.currentStatus : blueprint.status;
  const { data: logsData, isLoading: logsLoading } = useBlueprintLogs(
    showLogs ? blueprint.id : null,
    { pollingEnabled: !isFinalized },
  );

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  function handleDelete() {
    deleteBlueprint.mutate(blueprint.id, {
      onSuccess: () => {
        toast.success('Blueprint deleted');
        setConfirmDeleteOpen(false);
        onDeleted();
      },
      onError: (err: Error) => {
        toast.error('Failed to delete blueprint', { description: err.message });
        setConfirmDeleteOpen(false);
      },
    });
  }

  function handleEdit() {
    navigate(routes.blueprintNew, { state: { clone: blueprint } });
  }

  function handleClone() {
    navigate(routes.blueprintNew, { state: { clone: blueprint } });
  }

  function handleRebuild() {
    // Server-side replay from the stored parameters keeps every build input,
    // including build args and secrets the client can't fully see.
    rebuildBlueprint.mutate(blueprint.id, {
      onSuccess: () =>
        toast.success('Rebuild started', {
          description: 'A fresh build has been queued with the same configuration.',
        }),
      onError: (err: Error) =>
        toast.error('Failed to rebuild blueprint', { description: err.message }),
    });
  }

  function handleRebuildBase() {
    rebuildBase.mutate(undefined, {
      onSuccess: () =>
        toast.success('Base rebuild started', {
          description:
            'Rebuilding from the latest public base; dependent blueprints rebuild after it completes.',
        }),
      onError: (err: Error) =>
        toast.error('Failed to start base rebuild', { description: err.message }),
    });
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PanelHeader
        left={
          <>
            <Layers className="h-4 w-4 shrink-0 text-muted-foreground" />
            <PanelTitle as="h2" size="sm">
              {displayBlueprintName(blueprint.name)}
            </PanelTitle>
          </>
        }
        right={
          <>
            <ArchBadge architecture={blueprint.architecture} hideDefault />
            <BlueprintStatusBadge status={badgeStatus} />
            {isBaseBlueprint && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <Button
                      variant="ghost"
                      size="toolbar-icon"
                      onClick={handleRebuildBase}
                      disabled={rebuildBase.isPending || baseBuildInFlight}
                      aria-label="Rebuild base blueprint"
                      className="text-muted-foreground"
                    >
                      {rebuildBase.isPending ? (
                        <Spinner className="h-4 w-4" />
                      ) : (
                        <RefreshCw className={cn('h-4 w-4', baseBuildInFlight && 'animate-spin')} />
                      )}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {baseBuildInFlight ? 'Base image build in progress' : 'Rebuild from public base'}
                </TooltipContent>
              </Tooltip>
            )}
            {!isBaseBlueprint && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="toolbar-icon"
                    className="text-muted-foreground"
                    aria-label="More actions"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleEdit}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleClone}>
                    <Copy className="mr-2 h-4 w-4" />
                    Clone
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleRebuild} disabled={rebuildBlueprint.isPending}>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Rebuild
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    disabled={deleteBlueprint.isPending}
                    onClick={() => setConfirmDeleteOpen(true)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </>
        }
      />

      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-6">
          {isBaseBlueprint && (
            <PageDescription className="mt-0">
              Every organization has one host-managed blueprint named{' '}
              <span className="font-mono text-foreground/90">base</span>. It layers the core
              environment, setup commands contributed by your installed plugins, and your
              organization&apos;s own overrides, editable in Setup Commands below. When any of those
              change, base rebuilds, and blueprints built on it rebuild automatically.
            </PageDescription>
          )}
          <BlueprintDetailsSection
            blueprint={blueprint}
            blueprints={blueprints}
            onSelectBlueprint={onSelectBlueprint}
          />

          {blueprint.dockerfile && (
            <CollapsibleSection
              title="Dockerfile"
              icon={<FileCode className="h-3.5 w-3.5 text-muted-foreground/60" />}
            >
              <CodeBlock title="Dockerfile" maxHeight="16rem">
                {blueprint.dockerfile}
              </CodeBlock>
            </CollapsibleSection>
          )}

          {isBaseBlueprint ? (
            // The base blueprint replaces the flat "Setup Commands"
            // list with a layered editor: read-only host core (with
            // descriptions), read-only plugin contributions grouped per
            // plugin, and editable per-org overrides. Composed inline
            // so the surrounding sections (Dockerfile, File Mounts,
            // Build Logs) still render around it.
            <div data-tutorial-id="blueprint-base-layers">
              <BaseBlueprintLayersSection />
            </div>
          ) : (
            blueprint.systemSetupCommands &&
            blueprint.systemSetupCommands.length > 0 && (
              <CollapsibleSection
                title="Setup Commands"
                icon={<Terminal className="h-3.5 w-3.5 text-muted-foreground/60" />}
              >
                <CommandList commands={blueprint.systemSetupCommands} label="setup command" />
              </CollapsibleSection>
            )
          )}

          {blueprint.launchCommands && blueprint.launchCommands.length > 0 && (
            <CollapsibleSection
              title="Launch Commands"
              icon={<Play className="h-3.5 w-3.5 text-muted-foreground/60" />}
            >
              <CommandList commands={blueprint.launchCommands} label="launch command" />
            </CollapsibleSection>
          )}

          {blueprint.fileMounts && Object.keys(blueprint.fileMounts).length > 0 && (
            <CollapsibleSection
              title="File Mounts"
              icon={<FolderTree className="h-3.5 w-3.5 text-muted-foreground/60" />}
              defaultOpen={false}
            >
              <div className="space-y-2">
                {Object.entries(blueprint.fileMounts).map(([path, content]) => (
                  <CodeBlock key={path} title={path} maxHeight="12rem">
                    {content}
                  </CodeBlock>
                ))}
              </div>
            </CollapsibleSection>
          )}

          {blueprint.failureReason && (
            <CollapsibleSection title="Failure Reason">
              <div className="rounded-md border border-status-error/20 bg-status-error/5 p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-status-error shrink-0 mt-0.5" />
                  <PageDescription className="mt-0 text-sm text-status-error">
                    {blueprint.failureReason}
                  </PageDescription>
                </div>
              </div>
            </CollapsibleSection>
          )}

          {showLogs && (
            <BuildLogsSection
              logs={logsData?.logs}
              isLoading={logsLoading}
              blueprintId={blueprint.id}
              defaultOpen={!isFinalized}
            />
          )}
        </div>
      </div>

      <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {displayBlueprintName(blueprint.name)}?</AlertDialogTitle>
            <AlertDialogDescription>
              This blueprint will be permanently deleted. Any agents or personas currently using it
              will fail to launch until they are reconfigured with a different blueprint.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={handleDelete}>
              Delete blueprint
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function BlueprintEmptyState({ onCreate }: { onCreate?: () => void }) {
  return (
    <div
      data-testid="blueprint-empty-state"
      className="flex h-full min-h-0 items-center justify-center overflow-y-auto p-4 md:p-6"
    >
      <div className="w-full max-w-md space-y-5">
        <div className="flex flex-col items-center text-center">
          <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl border border-border/30">
            <Layers className="h-5 w-5 text-muted-foreground" />
          </div>
          <PanelTitle as="h2">Blueprints</PanelTitle>
          <PageDescription className="max-w-sm">
            A blueprint is a prebuilt devbox image (OS, packages, repository, and setup commands) so
            every agent starts in an identical, ready-to-work environment.
          </PageDescription>
        </div>

        <InfoPanel className="space-y-1.5 p-4">
          <SectionLabel as="h3">The base blueprint</SectionLabel>
          <PageDescription className="mt-0 text-xs">
            Every organization has one host-managed blueprint named{' '}
            <span className="font-mono text-foreground/90">base</span>. It layers the core
            environment, setup commands contributed by your installed plugins, and your
            organization&apos;s own overrides. When any of those change, base rebuilds, and
            blueprints built on it rebuild automatically.
          </PageDescription>
        </InfoPanel>

        <div className="space-y-2">
          <SectionLabel as="h3">What to do</SectionLabel>
          <ul className="space-y-1.5 text-xs text-muted-foreground">
            <li className="flex gap-2">
              <Layers className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                Select <span className="font-mono text-foreground/90">base</span> in the sidebar to
                inspect its layers or add org-wide setup commands.
              </span>
            </li>
            <li className="flex gap-2">
              <Plus className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                Create your own blueprint on top of base, and attach a repository so agents start
                with it cloned.
              </span>
            </li>
            <li className="flex gap-2">
              <Play className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>Pick a blueprint in the launch dialog when starting an agent.</span>
            </li>
          </ul>
        </div>

        {onCreate && (
          <div className="flex justify-center">
            <Button variant="outline" size="sm" onClick={onCreate} data-tutorial-id="blueprint-new">
              <Plus className="h-4 w-4" />
              New blueprint
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export function BlueprintLoadErrorState({ message }: { message?: string | null }) {
  return (
    <div data-testid="blueprint-detail-load-error">
      <EmptyState
        variant="fullHeight"
        icon={<AlertTriangle className="h-5 w-5 text-status-error" />}
        title={BLUEPRINT_LOAD_ERROR_TITLE}
        description={message ?? BLUEPRINT_LOAD_ERROR_DESCRIPTION}
      />
    </div>
  );
}
